/**
 * CrewStudio — single-pane status view for the 6 headless crew agents:
 * Wire · Scribe · Mercury · Quill · Frame · Stenographer.
 *
 * Left column:  crew roster (role, color, queue depth, last action).
 * Right column: latest run trace + a "Run Show Prep" trigger that posts
 *               a sample topic_candidate to /api/crew/run.
 *
 * Agents have no voice — this page is deliberately deterministic and
 * console-like, matching the "truth desk" aesthetic in the spec.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';
import { useAuth } from '../../auth/AuthContext';
import CrewShowPanel from './CrewShowPanel';

const CREW_TESTID = 'crew-studio';

const SAMPLE_TOPIC = {
  headline: 'Hurricane Kay upgrades in the Atlantic basin',
  region: 'atlantic',
  severity: 'storm',
  sources: [
    { service_id: 'svc.nhc', confidence: 0.95 },
    { service_id: 'svc.noaa_swpc', confidence: 0.9 },
  ],
};


function Badge({ children, color = '#4de2c0' }) {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '1px 6px',
        fontSize: 9,
        letterSpacing: '0.18em',
        background: `${color}15`,
        color,
        borderRadius: 3,
        border: `1px solid ${color}40`,
      }}
    >
      {children}
    </span>
  );
}

function CrewCard({ crew, onSelect, selected }) {
  const color = crew.color || '#4de2c0';
  return (
    <button
      type="button"
      onClick={() => onSelect(crew.name)}
      data-testid={`crew-card-${crew.name}`}
      style={{
        width: '100%',
        textAlign: 'left',
        padding: '12px 14px',
        background: selected ? `${color}12` : 'rgba(10, 14, 22, 0.6)',
        border: `1px solid ${selected ? color : 'rgba(120,150,200,0.15)'}`,
        borderRadius: 6,
        cursor: 'pointer',
        display: 'grid',
        gap: 6,
        transition: 'background 120ms ease, border-color 120ms ease',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ color, fontSize: 13, letterSpacing: '0.22em', fontWeight: 600 }}>
          {crew.name.toUpperCase()}
        </span>
        <Badge color={color}>{crew.role?.replace(/_/g, ' ').toUpperCase()}</Badge>
      </div>
      <div style={{ fontSize: 11, color: 'rgba(210,220,240,0.7)', lineHeight: 1.45 }}>
        {crew.headline || '—'}
      </div>
      <div style={{ display: 'flex', gap: 8, fontSize: 10, color: 'rgba(200,210,230,0.45)' }}>
        <span data-testid={`crew-queue-${crew.name}`}>QUEUE {crew.queue_depth ?? 0}</span>
        <span>·</span>
        <span>DONE {crew.completed_count ?? 0}</span>
        {crew.llm_allowed === false && (
          <>
            <span>·</span>
            <span style={{ color: '#f7c94b' }}>DETERMINISTIC</span>
          </>
        )}
      </div>
    </button>
  );
}

function TraceStage({ stage, index }) {
  return (
    <div
      data-testid={`crew-trace-stage-${index}`}
      style={{
        borderLeft: '2px solid rgba(120,150,200,0.3)',
        padding: '8px 12px 10px',
        marginLeft: 6,
        position: 'relative',
      }}
    >
      <span
        style={{
          position: 'absolute',
          left: -6,
          top: 12,
          width: 10,
          height: 10,
          borderRadius: 5,
          background: '#4de2c0',
          border: '2px solid #05070b',
        }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={{ fontSize: 10, letterSpacing: '0.22em', color: '#4de2c0' }}>
          {index + 1}. {stage.agent.toUpperCase()}
        </span>
        <span style={{ fontSize: 9, color: 'rgba(180,200,230,0.45)' }}>{stage.kind}</span>
      </div>
      <pre
        style={{
          margin: '4px 0 0',
          fontSize: 10,
          color: 'rgba(210,220,240,0.7)',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          fontFamily: 'ui-monospace, Menlo, monospace',
        }}
      >
{JSON.stringify(stage.output, null, 2)}
      </pre>
    </div>
  );
}

// Fallback crew roster used when the backend /api/crew endpoint returns 404
// (e.g. production VPS hasn't re-deployed with the new services layer).
// Matches the canonical definitions in backend/crew/*.py.
const FALLBACK_CREW = [
  { name: 'wire',         role: 'news_desk',    color: '#22d3ee', headline: 'News desk — breaking, trending, topic candidates, source grouping', owner_domains: ['breaking', 'trending', 'topics'],              llm_allowed: false, queue_depth: 0, completed_count: 0 },
  { name: 'scribe',       role: 'fact_desk',    color: '#a78bfa', headline: 'Verification desk — evidence packets and challenge flags',        owner_domains: ['verification', 'evidence', 'sources'],          llm_allowed: false, queue_depth: 0, completed_count: 0 },
  { name: 'mercury',      role: 'producer',     color: '#60a5fa', headline: 'Editorial gate — topic approval, run ordering',                   owner_domains: ['editorial', 'rundown', 'review'],              llm_allowed: true,  queue_depth: 0, completed_count: 0 },
  { name: 'quill',        role: 'writer',       color: '#f472b6', headline: 'Segment drafter — briefs, challenge segments, on-air copy',      owner_domains: ['drafting', 'segments', 'scripts'],             llm_allowed: true,  queue_depth: 0, completed_count: 0 },
  { name: 'frame',        role: 'scene',        color: '#fbbf24', headline: 'Scene cues — lower-thirds, severity framing, camera presets',    owner_domains: ['staging', 'scenes', 'overlays'],               llm_allowed: true,  queue_depth: 0, completed_count: 0 },
  { name: 'stenographer', role: 'stenographer', color: '#94a3b8', headline: 'Transcript + claim log — timestamps, predictions, archive hooks', owner_domains: ['transcript', 'claims', 'predictions', 'archive'], llm_allowed: false, queue_depth: 0, completed_count: 0 },
];

export default function CrewStudio({ onNavigate }) {
  const [status, setStatus] = useState(null);
  const [selected, setSelected] = useState('wire');
  const [run, setRun] = useState(null);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState(null);
  const [view, setView] = useState('show'); // 'show' (audience dialogue) | 'trace' (raw JSON)
  const [improvised, setImprovised] = useState(null); // LLM-generated bubbles when requested
  const [improvising, setImprovising] = useState(false);
  const [livePick, setLivePick] = useState(null);
  const [rundown, setRundown] = useState(null); // { runs: [...], count }
  const [buildingRundown, setBuildingRundown] = useState(false);
  const [producerRunBusy, setProducerRunBusy] = useState(false);
  const [producerRunResult, setProducerRunResult] = useState(null);
  const [newsroom, setNewsroom] = useState(null);
  const [autoShowMode, setAutoShowMode] = useState(false);
  const [autoShowSteps, setAutoShowSteps] = useState([]);
  const [adminTopicInput, setAdminTopicInput] = useState('');
  const [adminTopicBusy, setAdminTopicBusy] = useState(false);
  const [adminTopicSubmission, setAdminTopicSubmission] = useState(null);
  const [newTopicBusy, setNewTopicBusy] = useState(false);

  const auth = useAuth();
  // In dev, REACT_APP_AUTH_BYPASS already short-circuits AuthGate to render the
  // app without a real user; treat that as implicit admin so the topic input
  // is exercisable in local + cloud-agent walkthroughs.
  const localAuthBypass = String(process.env.REACT_APP_AUTH_BYPASS || '').toLowerCase() === 'true';
  const isAdmin = !!auth?.isAdmin || localAuthBypass;

  const normalizeError = (errorLike, fallbackMsg) => {
    const detail = errorLike?.response?.data?.detail;
    if (detail && typeof detail === 'object') {
      if (detail.error === 'NO_LLM_PROVIDER_CONFIGURED') return 'No active LLM provider configured.';
      return detail.message || fallbackMsg;
    }
    if (typeof detail === 'string' && detail) return detail;
    return errorLike?.message || fallbackMsg;
  };

  const loadNewsroom = async () => {
    try {
      const r = await api.get('/api/crew/newsroom/status');
      setNewsroom(r.data);
    } catch (_e) {
      // keep stale newsroom state
    }
  };

  const loadStatus = async () => {
    try {
      const [crewRes, newsroomRes] = await Promise.all([
        api.get('/api/crew/status'),
        api.get('/api/crew/newsroom/status').catch(() => ({ data: null })),
      ]);
      setStatus(crewRes.data);
      if (newsroomRes.data) setNewsroom(newsroomRes.data);
      setErr(null);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) {
        setErr('Crew API not deployed on this backend yet — redeploy pending. Showing local cache.');
      } else {
        setErr(normalizeError(e, 'failed to load crew status'));
      }
    }
  };

  useEffect(() => {
    loadStatus();
    const id = setInterval(loadStatus, 5000);
    return () => clearInterval(id);
  }, []);

  const handleRun = async () => {
    setRunning(true);
    setErr(null);
    setImprovised(null);
    setLivePick(null);
    try {
      const r = await api.post('/api/crew/run', { topic_candidate: SAMPLE_TOPIC });
      setRun(r.data);
      setProducerRunResult(r.data.producer_packet || null);
      setNewsroom(r.data.newsroom || null);
      await loadStatus();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) {
        setErr('Crew run API unavailable — backend redeploy pending.');
      } else {
        setErr(normalizeError(e, 'run failed'));
      }
    } finally {
      setRunning(false);
    }
  };

  const handleStartShow = async () => {
    setRunning(true);
    setErr(null);
    setImprovised(null);
    setRundown(null);
    try {
      const r = await api.post('/api/crew/start-show');
      setLivePick(r.data.live_pick || null);
      setRun(r.data.run || null);
      setProducerRunResult(r.data.producer_packet || null);
      setNewsroom(r.data.newsroom || null);
      await loadStatus();
    } catch (e) {
      const status = e?.response?.status;
      setErr(
        status === 503
          ? 'No live topic available right now — every public feed missed or timed out. Try again in a few minutes.'
          : normalizeError(e, 'start-show failed'),
      );
    } finally {
      setRunning(false);
    }
  };

  const handleAutoShowMode = async () => {
    setAutoShowMode(true);
    setRunning(true);
    setErr(null);
    setImprovised(null);
    setRundown(null);
    setAutoShowSteps([
      { id: 'fetch_trends', label: 'Fetch live trends', status: 'running' },
      { id: 'select_topic', label: 'Select topic', status: 'pending' },
      { id: 'open_show', label: 'Open show', status: 'pending' },
      { id: 'run_discussion', label: 'Run discussion', status: 'pending' },
      { id: 'producer_summary', label: 'Producer summary', status: 'pending' },
      { id: 'quill_script', label: 'Quill script', status: 'pending' },
    ]);
    try {
      const r = await api.post('/api/crew/auto-show', { host_agent: 'producer' });
      setLivePick(r.data.live_pick || null);
      setRun(r.data.run || null);
      setProducerRunResult(r.data.producer_packet || null);
      setNewsroom(r.data.newsroom || null);
      setAutoShowSteps(r.data.steps || []);
      await loadStatus();
    } catch (e) {
      setErr(normalizeError(e, 'auto show mode failed'));
    } finally {
      setRunning(false);
    }
  };

  const handleImprovise = async () => {
    if (!run) return;
    setImprovising(true);
    try {
      const r = await api.post('/api/crew/dialogue/improvise', { run });
      setImprovised(r.data.bubbles || []);
    } catch (e) {
      setErr(normalizeError(e, 'improvise failed'));
    } finally {
      setImprovising(false);
    }
  };

  const handleBuildRundown = async () => {
    setBuildingRundown(true);
    setErr(null);
    try {
      // Ingest a fresh NHC pull, but auto_draft through the full pipeline per storm.
      // If NHC is silent (off-season), fall back to analytics trending.
      const nhcRes = await api.post('/api/crew/wire/ingest', {
        basin: 'atlantic', max_items: 3, auto_draft: true,
      });
      let runs = (nhcRes.data.runs || []).filter((r) => !r.error);
      if (!runs.length) {
        // Try trending analytics fallback.
        const trending = await api.get('/api/producer/trending?limit=3');
        if ((trending.data.topic_candidates || []).length) {
          const rnd = await api.post('/api/crew/rundown', {
            topic_candidates: trending.data.topic_candidates,
          });
          runs = (rnd.data.runs || []).filter((r) => !r.error);
        }
      }
      // Final fallback: loop live-topic 3x so we get *something*.
      if (!runs.length) {
        const three = await Promise.all([0, 1, 2].map(() => api.post('/api/crew/start-show')));
        runs = three
          .map((res, i) => ({
            run_id: res.data.run?.run_id,
            topic_id: res.data.run?.topic_id,
            verdict: res.data.run?.verdict,
            decision: res.data.run?.decision,
            headline: res.data.live_pick?.candidate?.headline,
            severity: res.data.live_pick?.candidate?.severity,
            draft_text: (res.data.run?.trace || []).find((s) => s.agent === 'quill')?.output?.text || '',
            word_count: (res.data.run?.trace || []).find((s) => s.agent === 'quill')?.output?.word_count || 0,
            draft_state: (res.data.run?.trace || []).find((s) => s.agent === 'quill')?.output?.draft_state || 'stub',
            _run_full: res.data.run,
          }))
          .filter((r) => r.run_id);
      }
      setRundown({ runs, count: runs.length });
    } catch (e) {
      setErr(normalizeError(e, 'build rundown failed'));
    } finally {
      setBuildingRundown(false);
    }
  };

  const handleProducerRun = async () => {
    setProducerRunBusy(true);
    setErr(null);
    try {
      const r = await api.post('/api/runtime/producer/run?trigger=crew_ui_manual');
      setProducerRunResult((r.data || {}).episode || null);
      await loadNewsroom();
      await loadStatus();
    } catch (e) {
      setErr(normalizeError(e, 'producer run failed'));
    } finally {
      setProducerRunBusy(false);
    }
  };

  // "New Topic" — pull a fresh wire topic and force-bypass the duplicate hold
  // so a re-run never silently no-ops on Mercury's dedupe gate.
  const handleNewTopic = async () => {
    setNewTopicBusy(true);
    setErr(null);
    setImprovised(null);
    setRundown(null);
    try {
      const r = await api.post('/api/crew/auto-show', {
        host_agent: 'producer',
        force_new: true,
      });
      setLivePick(r.data.live_pick || null);
      setRun(r.data.run || null);
      setProducerRunResult(r.data.producer_packet || null);
      setNewsroom(r.data.newsroom || null);
      setAutoShowSteps(r.data.steps || []);
      await loadStatus();
    } catch (e) {
      const status = e?.response?.status;
      setErr(
        status === 503
          ? 'No live topic available right now — every public feed missed or timed out. Try again in a moment.'
          : normalizeError(e, 'new topic failed'),
      );
    } finally {
      setNewTopicBusy(false);
    }
  };

  // Admin "Enter Topic" — post the topic string directly to the crew pipeline.
  // No hardcoded notes / framing / preset content: Quill researches and briefs
  // entirely from the LLM call against the supplied headline.
  const handleAdminTopic = async () => {
    const headline = adminTopicInput.trim();
    if (!headline) return;
    setAdminTopicBusy(true);
    setErr(null);
    setImprovised(null);
    const submittedAt = new Date().toISOString();
    setAdminTopicSubmission({
      headline,
      submittedAt,
      status: 'wire_received',
      message: 'Wire received — submitting to crew',
      runId: null,
    });
    try {
      const candidate = {
        headline,
        source: 'admin.console',
        sources: [{ service_id: 'svc.admin_console', confidence: 0.7 }],
      };
      const r = await api.post('/api/crew/run', {
        topic_candidate: candidate,
        host_agent: 'producer',
        force_new: true,
      });
      const runData = r.data || {};
      setRun(runData);
      setProducerRunResult(runData?.producer_packet || null);
      setNewsroom(runData?.newsroom || null);
      const trace = Array.isArray(runData.trace) ? runData.trace : [];
      const lastStage = trace.length ? trace[trace.length - 1].agent : 'wire';
      setAdminTopicSubmission({
        headline,
        submittedAt,
        status: 'crew_complete',
        message: `Crew run complete — last stage: ${lastStage}`,
        runId: runData?.run_id || null,
      });
      setAdminTopicInput('');
      await loadStatus();
    } catch (e) {
      setAdminTopicSubmission((prev) => ({
        ...(prev || { headline, submittedAt }),
        status: 'failed',
        message: normalizeError(e, 'admin topic submission failed'),
        runId: null,
      }));
      setErr(normalizeError(e, 'admin topic submission failed'));
    } finally {
      setAdminTopicBusy(false);
    }
  };

  const clearAdminSubmission = () => {
    setAdminTopicSubmission(null);
    setAdminTopicInput('');
  };

  const crewList = status?.crew?.length ? status.crew : FALLBACK_CREW;
  const activeCrew = useMemo(
    () => crewList.find((c) => c.name === selected) || crewList[0],
    [crewList, selected]
  );

  const newsroomStatusLabel = (() => {
    const statusId = newsroom?.status || 'idle';
    if (statusId === 'crew_discussion_complete') return 'Crew discussion complete';
    if (statusId === 'producer_synthesizing') return 'Producer synthesizing episode';
    if (statusId === 'episode_packet_finalized') return 'Episode packet finalized';
    if (statusId === 'crew_debating') return 'Crew debating topic';
    return 'Newsroom idle';
  })();
  const quillDraftState = (() => {
    const stage = (run?.trace || []).find((s) => s.agent === 'quill');
    return stage?.output?.draft_state || 'idle';
  })();
  const segmentLocks = (producerRunResult?.segment || []).map((seg) => seg.lockState || 'draft');
  const hasFinalLock = segmentLocks.includes('final');

  return (
    <div
      data-testid={CREW_TESTID}
      style={{
        minHeight: '100vh',
        background: 'radial-gradient(circle at 20% -10%, rgba(30,40,60,0.4), #05070b 60%)',
        color: '#dbe3f2',
        padding: '16px 20px 40px',
        fontFamily: 'ui-monospace, Menlo, monospace',
      }}
    >
      {/* Header */}
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid rgba(120,150,200,0.18)',
          paddingBottom: 12,
          marginBottom: 18,
        }}
      >
        <div>
          <div style={{ fontSize: 10, letterSpacing: '0.34em', color: '#4de2c0' }}>
            SIGNAL · PRODUCTION CREW
          </div>
          <div style={{ fontSize: 22, letterSpacing: '0.05em', marginTop: 4 }}>
            Headless desk — Wire · Scribe · Mercury · Quill · Frame · Stenographer
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={handleAutoShowMode}
            disabled={running || buildingRundown}
            data-testid="crew-auto-show-btn"
            style={{
              background: autoShowMode ? 'linear-gradient(180deg, #f59e0b28, #7dd3fc12)' : 'rgba(245,158,11,0.12)',
              color: '#f8d28c',
              border: '1px solid rgba(245,158,11,0.65)',
              padding: '8px 16px',
              fontSize: 11,
              letterSpacing: '0.22em',
              borderRadius: 4,
              cursor: running || buildingRundown ? 'progress' : 'pointer',
              fontWeight: 700,
              boxShadow: autoShowMode ? '0 0 22px rgba(245,158,11,0.2)' : 'none',
            }}
          >
            {running && autoShowMode ? 'AUTO SHOW…' : 'AUTO SHOW MODE'}
          </button>
          <button
            type="button"
            onClick={handleStartShow}
            disabled={running || buildingRundown}
            data-testid="crew-start-show-btn"
            style={{
              background: 'linear-gradient(180deg, #d4a57418, #d4a57408)',
              color: '#f3e5c3',
              border: '1px solid #d4a574',
              padding: '8px 16px',
              fontSize: 11,
              letterSpacing: '0.22em',
              borderRadius: 4,
              cursor: running || buildingRundown ? 'progress' : 'pointer',
              fontWeight: 600,
            }}
          >
            {running ? 'ON AIR…' : '▶ START SHOW'}
          </button>
          <button
            type="button"
            onClick={handleProducerRun}
            disabled={running || buildingRundown || producerRunBusy}
            data-testid="crew-producer-run-btn"
            style={{
              background: '#22d3ee16',
              color: '#22d3ee',
              border: '1px solid #22d3ee55',
              padding: '8px 14px',
              fontSize: 11,
              letterSpacing: '0.22em',
              borderRadius: 4,
              cursor: running || buildingRundown || producerRunBusy ? 'progress' : 'pointer',
            }}
          >
            {producerRunBusy ? 'PRODUCER…' : 'RUN PRODUCER API'}
          </button>
          <button
            type="button"
            onClick={handleBuildRundown}
            disabled={running || buildingRundown}
            data-testid="crew-build-rundown-btn"
            style={{
              background: '#60a5fa14',
              color: '#60a5fa',
              border: '1px solid #60a5fa50',
              padding: '8px 14px',
              fontSize: 11,
              letterSpacing: '0.22em',
              borderRadius: 4,
              cursor: running || buildingRundown ? 'progress' : 'pointer',
            }}
          >
            {buildingRundown ? 'BUILDING…' : 'BUILD TODAY\'S RUNDOWN'}
          </button>
          <button
            type="button"
            onClick={handleRun}
            disabled={running || buildingRundown}
            data-testid="crew-run-btn"
            style={{
              background: '#4de2c015',
              color: '#4de2c0',
              border: '1px solid #4de2c050',
              padding: '8px 14px',
              fontSize: 11,
              letterSpacing: '0.22em',
              borderRadius: 4,
              cursor: running ? 'progress' : 'pointer',
            }}
          >
            {running ? 'RUNNING…' : 'DEMO RUN'}
          </button>
          {onNavigate && (
            <button
              type="button"
              onClick={() => onNavigate('nova')}
              data-testid="crew-back-btn"
              style={{
                background: 'transparent',
                color: 'rgba(200,210,230,0.7)',
                border: '1px solid rgba(120,150,200,0.25)',
                padding: '8px 14px',
                fontSize: 11,
                letterSpacing: '0.22em',
                borderRadius: 4,
                cursor: 'pointer',
              }}
            >
              ← BACK
            </button>
          )}
        </div>
      </header>

      {/* Topic controls row — "New Topic" and (admin-only) "Enter Topic". */}
      <section
        data-testid="crew-topic-controls"
        style={{
          display: 'flex',
          gap: 12,
          marginBottom: 14,
          padding: '12px 14px',
          background: 'rgba(15, 22, 36, 0.6)',
          border: '1px solid rgba(120, 150, 200, 0.18)',
          borderRadius: 8,
          flexWrap: 'wrap',
          alignItems: 'flex-start',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 220 }}>
          <span style={{
            fontSize: 9, letterSpacing: '0.22em', color: 'rgba(200,210,230,0.55)',
            textTransform: 'uppercase',
          }}>NEXT TOPIC</span>
          <button
            type="button"
            onClick={handleNewTopic}
            disabled={newTopicBusy || running || buildingRundown}
            data-testid="crew-new-topic-btn"
            style={{
              background: 'linear-gradient(180deg, rgba(34,211,238,0.18), rgba(110,231,183,0.10))',
              color: '#bae6fd',
              border: '1px solid rgba(34,211,238,0.55)',
              padding: '10px 16px',
              fontSize: 11,
              letterSpacing: '0.22em',
              borderRadius: 6,
              cursor: newTopicBusy ? 'progress' : 'pointer',
              fontWeight: 600,
              textTransform: 'uppercase',
              fontFamily: 'ui-monospace, Menlo, monospace',
            }}
          >
            {newTopicBusy ? 'FETCHING…' : '▶ NEW TOPIC'}
          </button>
          <span style={{
            fontSize: 9, color: 'rgba(200,210,230,0.45)',
            fontFamily: 'ui-monospace, Menlo, monospace', letterSpacing: '0.05em',
          }}>
            Bypasses duplicate hold · pulls fresh wire pick
          </span>
        </div>

        {isAdmin && (
          <div
            data-testid="crew-admin-topic-input"
            style={{
              display: 'flex', flexDirection: 'column', gap: 8, flex: 1, minWidth: 320,
            }}
          >
            <span style={{
              fontSize: 9, letterSpacing: '0.22em', color: '#fcd34d',
              textTransform: 'uppercase',
            }}>ADMIN · ENTER TOPIC</span>
            <form
              onSubmit={(e) => { e.preventDefault(); handleAdminTopic(); }}
              style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}
            >
              <input
                type="text"
                value={adminTopicInput}
                onChange={(e) => setAdminTopicInput(e.target.value)}
                placeholder="Topic line — submitted to Wire as-is"
                data-testid="crew-admin-topic-headline"
                disabled={adminTopicBusy}
                style={{
                  flex: 1,
                  background: 'rgba(8,12,20,0.7)',
                  color: '#e6edf7',
                  border: '1px solid rgba(252,211,77,0.35)',
                  padding: '8px 10px',
                  fontSize: 12,
                  fontFamily: 'Georgia, serif',
                  borderRadius: 4,
                  outline: 'none',
                  minWidth: 0,
                }}
              />
              <button
                type="submit"
                disabled={adminTopicBusy || !adminTopicInput.trim()}
                data-testid="crew-admin-topic-submit"
                style={{
                  background: 'linear-gradient(180deg, rgba(252,211,77,0.18), rgba(252,211,77,0.06))',
                  color: '#fcd34d',
                  border: '1px solid rgba(252,211,77,0.6)',
                  padding: '0 16px',
                  fontSize: 10,
                  letterSpacing: '0.22em',
                  borderRadius: 4,
                  cursor: adminTopicBusy ? 'progress' : 'pointer',
                  fontWeight: 600,
                  fontFamily: 'ui-monospace, Menlo, monospace',
                  whiteSpace: 'nowrap',
                }}
              >
                {adminTopicBusy ? 'SUBMITTING…' : 'SUBMIT'}
              </button>
            </form>

            {adminTopicSubmission && (
              <div
                data-testid="crew-admin-topic-confirmation"
                style={{
                  display: 'flex', flexDirection: 'column', gap: 4,
                  padding: '8px 10px',
                  background: adminTopicSubmission.status === 'failed'
                    ? 'rgba(248,113,113,0.08)'
                    : 'rgba(252,211,77,0.06)',
                  border: `1px solid ${adminTopicSubmission.status === 'failed'
                    ? 'rgba(248,113,113,0.35)'
                    : 'rgba(252,211,77,0.30)'}`,
                  borderRadius: 4,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                  <span style={{
                    fontSize: 9, letterSpacing: '0.18em',
                    color: adminTopicSubmission.status === 'failed' ? '#fca5a5' : '#fcd34d',
                    textTransform: 'uppercase',
                    fontFamily: 'ui-monospace, Menlo, monospace',
                  }}>
                    {adminTopicSubmission.status === 'failed' ? 'SUBMIT FAILED' : 'SUBMITTED'}
                  </span>
                  <span style={{
                    fontSize: 9, color: 'rgba(200,210,230,0.5)',
                    fontFamily: 'ui-monospace, Menlo, monospace',
                  }}>
                    {(() => { try { return new Date(adminTopicSubmission.submittedAt).toISOString().slice(11, 19) + 'Z'; } catch { return adminTopicSubmission.submittedAt; } })()}
                  </span>
                </div>
                <div
                  data-testid="crew-admin-topic-confirmation-headline"
                  style={{
                    fontFamily: 'Georgia, serif',
                    fontSize: 12,
                    color: '#e6edf7',
                    wordBreak: 'break-word',
                  }}
                >
                  {adminTopicSubmission.headline}
                </div>
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
                }}>
                  <span
                    data-testid="crew-admin-topic-confirmation-status"
                    style={{
                      fontSize: 10, color: 'rgba(200,210,230,0.7)',
                      fontFamily: 'ui-monospace, Menlo, monospace',
                    }}
                  >
                    {adminTopicSubmission.message}
                    {adminTopicSubmission.runId && (
                      <span style={{ color: 'rgba(200,210,230,0.5)', marginLeft: 6 }}>
                        · run {adminTopicSubmission.runId}
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={clearAdminSubmission}
                    data-testid="crew-admin-topic-clear"
                    style={{
                      background: 'transparent',
                      color: 'rgba(200,210,230,0.55)',
                      border: '1px solid rgba(120,150,200,0.25)',
                      padding: '2px 8px',
                      fontSize: 9,
                      letterSpacing: '0.2em',
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontFamily: 'ui-monospace, Menlo, monospace',
                    }}
                  >
                    CLEAR
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {err && (
        <div
          data-testid="crew-error"
          style={{ color: '#f87171', marginBottom: 10, fontSize: 12 }}
        >
          {err}
        </div>
      )}
      {producerRunResult && (
        <div
          data-testid="crew-producer-run-result"
          style={{ color: '#7dd3fc', marginBottom: 10, fontSize: 12 }}
        >
          Producer packet linked · episode {producerRunResult.episodeId || producerRunResult.episode_id || 'pending'} · segments:{' '}
          {(producerRunResult.segment || []).length || (producerRunResult.episode_context?.segments || []).length} · scripts:{' '}
          {(producerRunResult.scriptFrame || []).length || (producerRunResult.episode_context?.scripts || []).length}
        </div>
      )}
      {autoShowMode && autoShowSteps.length > 0 && (
        <section
          data-testid="crew-auto-show-steps"
          style={{
            marginBottom: 12,
            padding: '10px 12px',
            borderRadius: 8,
            background: 'rgba(245,158,11,0.08)',
            border: '1px solid rgba(245,158,11,0.28)',
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <span style={{ fontSize: 10, letterSpacing: '0.22em', color: '#f8d28c' }}>AUTO SHOW MODE</span>
          {autoShowSteps.map((step) => (
            <Badge key={step.id} color={step.status === 'complete' ? '#86efac' : step.status === 'degraded' ? '#fbbf24' : '#7dd3fc'}>
              {step.label} · {String(step.status || 'pending').toUpperCase()}
            </Badge>
          ))}
        </section>
      )}
      <section
        data-testid="crew-newsroom-status"
        style={{
          marginBottom: 12,
          border: '1px solid rgba(77,226,192,0.25)',
          borderRadius: 6,
          background: 'rgba(8,16,24,0.55)',
          padding: '10px 12px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ color: '#4de2c0', fontSize: 11, letterSpacing: '0.18em' }}>NEWSROOM STATUS</span>
          <span style={{ color: '#e8f7ff', fontSize: 12 }}>{newsroomStatusLabel}</span>
        </div>
        <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 10 }}>
          <Badge color={newsroom?.status === 'crew_debating' ? '#fca5a5' : '#8aa2c7'}>
            Crew debating
          </Badge>
          <Badge color={quillDraftState === 'llm' || quillDraftState === 'active' ? '#fbbf24' : '#8aa2c7'}>
            Quill drafting
          </Badge>
          <Badge color={hasFinalLock ? '#22c55e' : '#8aa2c7'}>
            Segment lock/finalization
          </Badge>
          <Badge color={newsroom?.status === 'crew_discussion_complete' || newsroom?.status === 'episode_packet_finalized' ? '#4de2c0' : '#8aa2c7'}>
            Crew discussion complete
          </Badge>
          <Badge color={newsroom?.status === 'producer_synthesizing' ? '#7dd3fc' : '#8aa2c7'}>
            Producer synthesizing episode
          </Badge>
          <Badge color={newsroom?.status === 'episode_packet_finalized' ? '#86efac' : '#8aa2c7'}>
            Episode packet finalized
          </Badge>
        </div>
        {Array.isArray(newsroom?.timeline) && newsroom.timeline.length > 0 && (
          <div style={{ marginTop: 8, fontSize: 10, color: 'rgba(205,222,245,0.8)' }}>
            Latest: {newsroom.timeline[newsroom.timeline.length - 1].message}
          </div>
        )}
        {Array.isArray(producerRunResult?.segment) && producerRunResult.segment.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <div style={{ color: '#93c5fd', fontSize: 10, letterSpacing: '0.16em', marginBottom: 4 }}>
              ACTIVE SEGMENT QUEUE
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {producerRunResult.segment.map((seg, idx) => (
                <Badge key={`${seg.name}-${idx}`} color={seg.lockState === 'final' ? '#22c55e' : '#7dd3fc'}>
                  {seg.order || idx + 1}. {String(seg.name || 'segment').replace(/_/g, ' ')}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* 2-column layout */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '320px 1fr',
          gap: 18,
          alignItems: 'start',
        }}
      >
        {/* Left — roster */}
        <div data-testid="crew-roster" style={{ display: 'grid', gap: 8 }}>
          <div style={{ fontSize: 10, letterSpacing: '0.24em', color: 'rgba(180,200,230,0.55)', marginBottom: 4 }}>
            ROSTER · {crewList.length} AGENTS
          </div>
          {crewList.map((c) => (
            <CrewCard
              key={c.name}
              crew={c}
              onSelect={setSelected}
              selected={selected === c.name}
            />
          ))}
          {crewList.length === 0 && (
            <div style={{ fontSize: 11, color: 'rgba(200,210,230,0.4)' }}>Loading roster…</div>
          )}
        </div>

        {/* Right — detail + last run trace */}
        <div style={{ display: 'grid', gap: 14 }}>
          {/* Selected agent detail */}
          {activeCrew && (
            <section
              data-testid={`crew-detail-${activeCrew.name}`}
              style={{
                padding: '14px 18px',
                background: 'rgba(10,14,22,0.6)',
                border: `1px solid ${activeCrew.color || '#4de2c0'}40`,
                borderRadius: 6,
              }}
            >
              <div style={{ color: activeCrew.color || '#4de2c0', fontSize: 13, letterSpacing: '0.28em', marginBottom: 4 }}>
                {activeCrew.name.toUpperCase()} · {activeCrew.role?.replace(/_/g, ' ').toUpperCase()}
              </div>
              <div style={{ fontSize: 13, color: 'rgba(220,230,245,0.88)', lineHeight: 1.5, marginBottom: 10 }}>
                {activeCrew.headline}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, fontSize: 11 }}>
                <div>
                  <div style={{ color: 'rgba(180,200,230,0.45)', fontSize: 9, letterSpacing: '0.18em' }}>QUEUE</div>
                  <div style={{ fontSize: 16, color: '#e8e8e8' }}>{activeCrew.queue_depth ?? 0}</div>
                </div>
                <div>
                  <div style={{ color: 'rgba(180,200,230,0.45)', fontSize: 9, letterSpacing: '0.18em' }}>COMPLETED</div>
                  <div style={{ fontSize: 16, color: '#e8e8e8' }}>{activeCrew.completed_count ?? 0}</div>
                </div>
                <div>
                  <div style={{ color: 'rgba(180,200,230,0.45)', fontSize: 9, letterSpacing: '0.18em' }}>LLM POLICY</div>
                  <div style={{ fontSize: 12, color: activeCrew.llm_allowed === false ? '#f7c94b' : '#4ade80' }}>
                    {activeCrew.llm_allowed === false ? 'DENIED' : 'ALLOWED'}
                  </div>
                </div>
                <div>
                  <div style={{ color: 'rgba(180,200,230,0.45)', fontSize: 9, letterSpacing: '0.18em' }}>DOMAINS</div>
                  <div style={{ fontSize: 11, color: '#e8e8e8' }}>
                    {(activeCrew.owner_domains || []).join(' · ') || '—'}
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Last run — Show dialogue (default) or raw trace (debug) */}
          <section
            data-testid="crew-trace"
            style={{
              padding: '14px 18px',
              background: 'rgba(10,14,22,0.6)',
              border: '1px solid rgba(120,150,200,0.18)',
              borderRadius: 6,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
              <div style={{ display: 'flex', gap: 4 }}>
                {['show', 'trace'].map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setView(v)}
                    data-testid={`crew-view-${v}`}
                    style={{
                      background: view === v ? 'rgba(212,165,116,0.14)' : 'transparent',
                      color: view === v ? '#d4a574' : 'rgba(200,210,230,0.5)',
                      border: `1px solid ${view === v ? '#d4a57455' : 'rgba(120,150,200,0.18)'}`,
                      padding: '5px 14px',
                      fontSize: 10,
                      letterSpacing: '0.24em',
                      borderRadius: 3,
                      cursor: 'pointer',
                      fontFamily: 'ui-monospace, Menlo, monospace',
                    }}
                  >
                    {v === 'show' ? 'SHOW' : 'DEBUG'}
                  </button>
                ))}
                {run && view === 'show' && (
                  <button
                    type="button"
                    onClick={handleImprovise}
                    disabled={improvising}
                    data-testid="crew-improvise-btn"
                    style={{
                      background: improvised ? 'rgba(167,139,250,0.18)' : 'transparent',
                      color: improvised ? '#c4b5fd' : 'rgba(167,139,250,0.75)',
                      border: '1px solid rgba(167,139,250,0.45)',
                      padding: '5px 14px',
                      fontSize: 10,
                      letterSpacing: '0.24em',
                      borderRadius: 3,
                      cursor: improvising ? 'progress' : 'pointer',
                      marginLeft: 6,
                      fontFamily: 'ui-monospace, Menlo, monospace',
                    }}
                  >
                    {improvising ? '…IMPROVISING' : improvised ? '★ IMPROVISED' : '✨ IMPROVISE'}
                  </button>
                )}
              </div>
              {run && (
                <span style={{ fontSize: 10, color: 'rgba(200,210,230,0.55)' }}>
                  run_id={run.run_id} · {run.verdict} · {run.decision}
                </span>
              )}
            </div>

            {livePick && (
              <div
                data-testid="crew-live-pick"
                style={{
                  padding: '8px 12px',
                  background: 'rgba(212,165,116,0.08)',
                  border: '1px dashed rgba(212,165,116,0.35)',
                  borderRadius: 4,
                  fontSize: 11,
                  color: '#d4a574',
                  marginBottom: 12,
                }}
              >
                LIVE PICK · source {livePick.source.toUpperCase()} · picked {livePick.picked_at?.slice(11, 19)}Z · "{livePick.candidate?.headline}"
              </div>
            )}

            {!run && (
              <div style={{ color: 'rgba(200,210,230,0.45)', fontSize: 13, fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>
                Click <strong style={{ color: '#d4a574', fontStyle: 'normal' }}>▶ START SHOW</strong> to fetch a live topic and let the crew talk it through,
                or <strong style={{ color: '#60a5fa', fontStyle: 'normal' }}>BUILD TODAY'S RUNDOWN</strong> for a full multi-segment show.
              </div>
            )}

            {run && view === 'show' && <CrewShowPanel run={run} improvisedBubbles={improvised} running={running} />}

            {run && view === 'trace' && (
              <div>
                {run.trace.map((s, i) => (
                  <TraceStage key={i} stage={s} index={i} />
                ))}
                <div style={{ marginTop: 10, fontSize: 10, color: 'rgba(200,210,230,0.45)' }}>
                  Pipeline: {run.pipeline}
                </div>
              </div>
            )}
          </section>

          {/* Rundown stack — multiple segments from BUILD TODAY'S RUNDOWN */}
          {rundown && (
            <section
              data-testid="crew-rundown-stack"
              style={{
                padding: '14px 18px',
                background: 'rgba(10,14,22,0.6)',
                border: '1px solid rgba(96,165,250,0.25)',
                borderRadius: 6,
              }}
            >
              <div style={{
                fontSize: 11, letterSpacing: '0.24em', color: '#60a5fa',
                marginBottom: 12, display: 'flex', justifyContent: 'space-between',
              }}>
                <span>TODAY'S RUNDOWN · {rundown.count} SEGMENTS</span>
                <button
                  type="button"
                  onClick={() => setRundown(null)}
                  data-testid="crew-rundown-close-btn"
                  style={{
                    background: 'transparent', color: 'rgba(200,210,230,0.55)',
                    border: '1px solid rgba(120,150,200,0.2)',
                    padding: '3px 10px', fontSize: 9, cursor: 'pointer', borderRadius: 3,
                  }}
                >
                  CLEAR
                </button>
              </div>
              {rundown.runs.map((r, i) => (
                <article
                  key={r.run_id || i}
                  data-testid={`rundown-segment-${i}`}
                  style={{
                    marginBottom: 14,
                    padding: '14px 18px',
                    background: 'rgba(24,20,13,0.7)',
                    border: '1px solid rgba(212,165,116,0.22)',
                    borderRadius: 6,
                  }}
                >
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                    marginBottom: 8, gap: 10, flexWrap: 'wrap',
                  }}>
                    <span style={{ fontSize: 11, letterSpacing: '0.22em', color: '#60a5fa' }}>
                      SEGMENT {i + 1}
                    </span>
                    <span style={{ fontSize: 10, color: 'rgba(200,210,230,0.6)' }}>
                      {r.severity?.toUpperCase()} · {r.verdict} · {r.decision} · {r.word_count} words
                    </span>
                  </div>
                  <div style={{
                    fontFamily: 'Georgia, serif', color: '#f3e5c3', fontSize: 13,
                    fontStyle: 'italic', marginBottom: 8,
                  }}>
                    {r.headline}
                  </div>
                  <div
                    data-testid={`rundown-draft-${i}`}
                    style={{
                      fontFamily: 'Georgia, "Iowan Old Style", serif', color: '#f3e5c3',
                      fontSize: 15, lineHeight: 1.7, whiteSpace: 'pre-wrap',
                    }}
                  >
                    {r.draft_text || '[no draft]'}
                  </div>
                </article>
              ))}
            </section>
          )}

          {/* Quill script — shown only in DEBUG view */}
          {run && view === 'trace' && (() => {
            const quillStage = (run.trace || []).find((s) => s.agent === 'quill');
            const draftText = quillStage?.output?.text || '';
            if (!draftText) return null;
            const isStub = draftText.startsWith('[offline-stub]') || draftText.startsWith('[llm-error]');
            return (
              <section
                data-testid="crew-script-preview"
                style={{
                  padding: '18px 22px',
                  background: isStub ? 'rgba(70,40,20,0.35)' : 'rgba(24,20,13,0.85)',
                  border: `1px solid ${isStub ? 'rgba(247,201,75,0.45)' : 'rgba(212,165,116,0.3)'}`,
                  borderRadius: 8,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
                  <span style={{ fontSize: 11, letterSpacing: '0.24em', color: '#d4a574' }}>
                    QUILL · DRAFT SEGMENT
                  </span>
                  <span style={{ fontSize: 10, color: 'rgba(212,165,116,0.6)' }}>
                    {quillStage.output.word_count} words · {quillStage.output.draft_state} · {quillStage.output.segment}
                  </span>
                </div>
                {isStub && (
                  <div style={{
                    fontSize: 11, color: '#f7c94b', marginBottom: 8, letterSpacing: '0.06em',
                  }}>
                    ⚠ LLM provider unavailable or errored — showing stub. Configure ANTHROPIC_API_KEY, GROK_API_KEY, or OPENAI_API_KEY on the backend.
                  </div>
                )}
                <div
                  data-testid="crew-script-text"
                  style={{
                    fontFamily: 'Georgia, "Iowan Old Style", serif',
                    color: '#f3e5c3',
                    fontSize: 16,
                    lineHeight: 1.7,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {draftText}
                </div>
                <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                  <button
                    data-testid="crew-copy-script-btn"
                    onClick={() => navigator.clipboard?.writeText(draftText).catch(() => null)}
                    style={{
                      background: 'transparent',
                      color: '#d4a574',
                      border: '1px solid rgba(212,165,116,0.35)',
                      padding: '5px 12px',
                      fontSize: 11,
                      letterSpacing: '0.08em',
                      cursor: 'pointer',
                      borderRadius: 3,
                      fontFamily: 'Georgia, serif',
                    }}
                  >
                    COPY TO CLIPBOARD
                  </button>
                  {onNavigate && (
                    <button
                      data-testid="crew-open-in-quill-btn"
                      onClick={() => onNavigate('quill')}
                      style={{
                        background: 'transparent',
                        color: '#d4a574',
                        border: '1px solid rgba(212,165,116,0.35)',
                        padding: '5px 12px',
                        fontSize: 11,
                        letterSpacing: '0.08em',
                        cursor: 'pointer',
                        borderRadius: 3,
                        fontFamily: 'Georgia, serif',
                      }}
                    >
                      OPEN IN QUILL STUDIO →
                    </button>
                  )}
                </div>
              </section>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
